import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { useMutation, useLazyQuery } from '@apollo/client/react';
import { PaystackButton } from 'react-paystack';
import moment from 'moment';
import { Notify } from 'notiflix';

import { REGISTER_USER, LOGIN_USER, CREATE_EVENT_PAYMENT, REGISTER_FOR_EVENT } from 'helper/graphql/mutations';
import { GET_USER_PUBLIC_PROFILE, GET_USER_DETAILS } from 'helper/graphql/queries';
import { getUserGUID, setSession } from 'utils/jwt';
import useAuth from 'hooks/useAuth';

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatTypeLabel = (eventType) => {
	if (!eventType) return 'Event';
	return eventType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatPrice = (price) => {
	if (!price || price === 0) return null;
	return `Ksh ${price.toLocaleString()}`;
};

const generateRandomPassword = () =>
	Math.random().toString(36).slice(-10) + Math.random().toString(36).slice(-4).toUpperCase();

// ─── Step 1: Auth ────────────────────────────────────────────────────────────

const AuthStep = ({ onAuthenticated }) => {
	const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', password: '' });
	const [isUserRegistered, setIsUserRegistered] = useState(false);
	const [isProcessing, setIsProcessing] = useState(false);

	const [onLoginUser] = useMutation(LOGIN_USER);
	const [onRegisterUser] = useMutation(REGISTER_USER);
	const [checkUserByEmail] = useLazyQuery(GET_USER_PUBLIC_PROFILE);
	const [getUserInfo] = useLazyQuery(GET_USER_DETAILS);

	const handleChange = (e) =>
		setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

	const loginAndBootstrap = async (email, password) => {
		const res = await onLoginUser({ variables: { username: email, password } });
		if (res.errors) throw new Error('Login failed');

		const token = res.data.loginUser.token;
		setSession(token);
		localStorage.setItem('isAuthenticated', 'true');
		localStorage.setItem('accessToken', token);

		await getUserInfo({
			variables: { guid: getUserGUID(token) },
			onCompleted: (data) => {
				localStorage.setItem('user', JSON.stringify(data?.allUsers?.edges[0]?.node || ''));
				onAuthenticated();
			},
		});
	};

	const handleCheckEmail = async () => {
		setIsProcessing(true);
		try {
			const { data } = await checkUserByEmail({ variables: { email: formData.email } });
			const userExists = !!data?.getUserPublicInfo;

			if (userExists) {
				setIsUserRegistered(true);
				return;
			}

			const password = generateRandomPassword();
			const registerRes = await onRegisterUser({
				variables: {
					email: formData.email,
					firstName: formData.firstName,
					lastName: formData.lastName,
					password,
					country: '',
					gender: '',
					state: '',
					phoneNumber: '',
					isAdmin: false,
					isHealthcareWorker: false,
					isOrgAdmin: false,
					isPatient: false,
					isPrivacyPolicyGiven: true,
					isConsentGiven: true,
				},
			});

			if (!registerRes.data?.createUser?.user) throw new Error('Registration failed');
			await loginAndBootstrap(formData.email, password);
		} catch (err) {
			Notify.failure(err?.message || 'Something went wrong. Please try again.');
		} finally {
			setIsProcessing(false);
		}
	};

	const handleLoginExisting = async () => {
		if (!formData.password) return;
		setIsProcessing(true);
		try {
			await loginAndBootstrap(formData.email, formData.password);
		} catch {
			Notify.failure('Login failed. Please check your credentials.');
		} finally {
			setIsProcessing(false);
		}
	};

	return (
		<div className="epm-step">
			<div className="epm-step-header">
				<div className="epm-step-icon-wrap">
					<i className="fe fe-user epm-step-icon"></i>
				</div>
				<h4 className="epm-step-title">
					{isUserRegistered ? 'Welcome back!' : 'Sign in to continue'}
				</h4>
				<p className="epm-step-sub">
					{isUserRegistered
						? 'Enter your password to proceed to payment'
						: 'Create an account or sign in to register'}
				</p>
			</div>

			<Form>
				{!isUserRegistered && (
					<Row className="g-3 mb-3">
						<Col md={6}>
							<Form.Group>
								<Form.Label className="epm-label">First Name <span className="text-danger">*</span></Form.Label>
								<Form.Control type="text" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="John" className="epm-input" />
							</Form.Group>
						</Col>
						<Col md={6}>
							<Form.Group>
								<Form.Label className="epm-label">Last Name <span className="text-danger">*</span></Form.Label>
								<Form.Control type="text" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Doe" className="epm-input" />
							</Form.Group>
						</Col>
					</Row>
				)}

				<Form.Group className="mb-3">
					<Form.Label className="epm-label">Email Address <span className="text-danger">*</span></Form.Label>
					<Form.Control type="email" name="email" value={formData.email} onChange={handleChange} placeholder="john@example.com" className="epm-input" disabled={isUserRegistered} />
					{isUserRegistered && (
						<Form.Text className="text-muted d-flex align-items-center mt-2">
							<i className="fe fe-check-circle text-success me-2"></i>
							Account found! Enter your password to continue.
						</Form.Text>
					)}
				</Form.Group>

				{isUserRegistered && (
					<Form.Group className="mb-4">
						<Form.Label className="epm-label">Password <span className="text-danger">*</span></Form.Label>
						<Form.Control type="password" name="password" value={formData.password} onChange={handleChange} placeholder="Enter your password" className="epm-input" />
					</Form.Group>
				)}

				{!isUserRegistered && (
					<div className="epm-privacy-note mb-4">
						<i className="fe fe-shield text-success me-2"></i>
						<small className="text-muted">By continuing you agree to our Terms of Service and Privacy Policy.</small>
					</div>
				)}

				<Button
					className="epm-primary-btn w-100"
					disabled={isProcessing || !formData.email || (!isUserRegistered && (!formData.firstName || !formData.lastName))}
					onClick={isUserRegistered ? handleLoginExisting : handleCheckEmail}
				>
					{isProcessing ? (
						<><span className="spinner-border spinner-border-sm me-2"></span>Processing...</>
					) : isUserRegistered ? (
						<><i className="fe fe-log-in me-2"></i>Login &amp; Continue</>
					) : (
						<><i className="fe fe-arrow-right me-2"></i>Continue</>
					)}
				</Button>
			</Form>
		</div>
	);
};

// ─── Step 2: Attendance mode (only shown when both prices exist) ─────────────

const ModeStep = ({ virtualPrice, physicalPrice, onSelect }) => (
	<div className="epm-step">
		<div className="epm-step-header">
			<div className="epm-step-icon-wrap" style={{ background: 'linear-gradient(135deg, #1d9eb0, #0e7490)' }}>
				<i className="fe fe-layers epm-step-icon"></i>
			</div>
			<h4 className="epm-step-title">Choose Attendance Mode</h4>
			<p className="epm-step-sub">Select how you will attend this event</p>
		</div>

		<div className="epm-mode-cards">
			<button className="epm-mode-card" onClick={() => onSelect('virtual', virtualPrice)}>
				<div className="epm-mode-icon epm-mode-virtual">
					<i className="fe fe-monitor"></i>
				</div>
				<div>
					<div className="epm-mode-label">Virtual / Online</div>
					<div className="epm-mode-price">{formatPrice(virtualPrice)}</div>
					<div className="epm-mode-desc">Attend via live stream from anywhere</div>
				</div>
				<i className="fe fe-arrow-right epm-mode-arrow"></i>
			</button>

			<button className="epm-mode-card" onClick={() => onSelect('physical', physicalPrice)}>
				<div className="epm-mode-icon epm-mode-physical">
					<i className="fe fe-map-pin"></i>
				</div>
				<div>
					<div className="epm-mode-label">In-Person</div>
					<div className="epm-mode-price">{formatPrice(physicalPrice)}</div>
					<div className="epm-mode-desc">Attend at the physical venue</div>
				</div>
				<i className="fe fe-arrow-right epm-mode-arrow"></i>
			</button>
		</div>
	</div>
);

// ─── Step 3: Payment ─────────────────────────────────────────────────────────

const PaymentStep = ({ event, selectedMode, selectedPrice, user, onSuccess, onBack }) => {
	const sessionDate = moment(event.sessionDatetime);

	const [createEventPayment] = useMutation(CREATE_EVENT_PAYMENT);
	const [registerForEvent] = useMutation(REGISTER_FOR_EVENT);
	const [processing, setProcessing] = useState(false);

	const handlePaystackSuccess = async () => {
		setProcessing(true);
		try {
			await createEventPayment({
				variables: {
					nextPaymentDate: moment().add(1, 'year').toISOString(),
					userId: user.guid,
					eventId: event.guid,
					amount: selectedPrice,
				},
			});

			await registerForEvent({ variables: { eventGuid: event.guid } });
			onSuccess();
		} catch (err) {
			Notify.failure(err?.message || 'Payment recorded but registration failed. Please contact support.');
			setProcessing(false);
		}
	};

	const handlePaystackClose = () => {
		Notify.failure('Payment was cancelled.');
	};

	const paystackConfig = {
		email: user.email,
		amount: selectedPrice * 100, // Paystack expects kobo (Ksh × 100)
		currency: 'KES',
		publicKey: process.env.REACT_APP_PAYSTACK_KEY,
		text: `Pay ${formatPrice(selectedPrice)}`,
		metadata: {
			name: `${user.firstName} ${user.lastName}`,
			phone: user.phoneNumber || '',
			custom_fields: [
				{ display_name: 'Event', variable_name: 'event', value: event.title },
				{ display_name: 'Attendance Mode', variable_name: 'mode', value: selectedMode || 'virtual' },
			],
		},
		onSuccess: handlePaystackSuccess,
		onClose: handlePaystackClose,
	};

	return (
		<div className="epm-step">
			<div className="epm-step-header">
				<div className="epm-step-icon-wrap" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
					<i className="fe fe-credit-card epm-step-icon"></i>
				</div>
				<h4 className="epm-step-title">Complete Payment</h4>
				<p className="epm-step-sub">Secure payment via Paystack</p>
			</div>

			{/* Order summary */}
			<div className="epm-order-summary mb-4">
				<div className="epm-summary-row">
					<span className="epm-summary-label">Event</span>
					<span className="epm-summary-value">{event.title}</span>
				</div>
				<div className="epm-summary-row">
					<span className="epm-summary-label">Date</span>
					<span className="epm-summary-value">{sessionDate.format('MMM D, YYYY · h:mm A')}</span>
				</div>
				{selectedMode && (
					<div className="epm-summary-row">
						<span className="epm-summary-label">Attendance</span>
						<span className="epm-summary-value" style={{ textTransform: 'capitalize' }}>
							{selectedMode === 'virtual' ? (
								<><i className="fe fe-monitor me-1" style={{ color: '#10b981' }}></i>Virtual</>
							) : (
								<><i className="fe fe-map-pin me-1" style={{ color: '#F68521' }}></i>In-Person</>
							)}
						</span>
					</div>
				)}
				<div className="epm-summary-divider"></div>
				<div className="epm-summary-row epm-summary-total">
					<span className="epm-summary-label">Total</span>
					<span className="epm-summary-total-amount">{formatPrice(selectedPrice)}</span>
				</div>
			</div>

			{processing ? (
				<Button className="epm-primary-btn w-100" disabled>
					<span className="spinner-border spinner-border-sm me-2"></span>
					Confirming registration…
				</Button>
			) : (
				<PaystackButton className="epm-paystack-btn w-100" {...paystackConfig} />
			)}

			{selectedMode && (
				<button className="epm-back-link mt-3" onClick={onBack}>
					<i className="fe fe-arrow-left me-1"></i>Change attendance mode
				</button>
			)}

			<div className="epm-secure-note mt-3">
				<i className="fe fe-lock me-2"></i>
				Secured by Paystack · Your card details are never stored
			</div>
		</div>
	);
};

// ─── Main Modal ───────────────────────────────────────────────────────────────

const EventPaymentModal = ({ show, onHide, event, onSuccess }) => {
	const { isAuthenticated } = useAuth();

	const virtualPrice = event?.price || 0;
	const physicalPrice = event?.physicalPrice || 0;
	const hasBothPrices = virtualPrice > 0 && physicalPrice > 0;
	const singlePrice = virtualPrice > 0 ? virtualPrice : physicalPrice;
	const singleMode = virtualPrice > 0 ? 'virtual' : 'physical';

	// Steps: 'auth' | 'mode' | 'payment'
	const initialStep = isAuthenticated ? (hasBothPrices ? 'mode' : 'payment') : 'auth';
	const [step, setStep] = useState(initialStep);
	const [selectedMode, setSelectedMode] = useState(hasBothPrices ? null : singleMode);
	const [selectedPrice, setSelectedPrice] = useState(hasBothPrices ? null : singlePrice);

	// Current user from localStorage
	const [user, setUser] = useState(null);

	useEffect(() => {
		if (show) {
			setStep(isAuthenticated ? (hasBothPrices ? 'mode' : 'payment') : 'auth');
			setSelectedMode(hasBothPrices ? null : singleMode);
			setSelectedPrice(hasBothPrices ? null : singlePrice);
			// Load user from localStorage
			try {
				const stored = localStorage.getItem('user');
				if (stored) setUser(JSON.parse(stored));
			} catch { /* ignore */ }
		}
	}, [show, isAuthenticated]);

	const handleAuthenticated = () => {
		try {
			const stored = localStorage.getItem('user');
			if (stored) setUser(JSON.parse(stored));
		} catch { /* ignore */ }
		setStep(hasBothPrices ? 'mode' : 'payment');
	};

	const handleModeSelect = (mode, price) => {
		setSelectedMode(mode);
		setSelectedPrice(price);
		setStep('payment');
	};

	const handlePaymentSuccess = () => {
		Notify.success('Payment successful! You are now registered.');
		onSuccess();
		onHide();
	};

	if (!event) return null;

	const steps = [
		...(!isAuthenticated ? [{ key: 'auth', label: 'Account', icon: 'fe-user' }] : []),
		...(hasBothPrices ? [{ key: 'mode', label: 'Mode', icon: 'fe-layers' }] : []),
		{ key: 'payment', label: 'Payment', icon: 'fe-credit-card' },
	];
	const currentIdx = steps.findIndex((s) => s.key === step);

	return (
		<Modal show={show} onHide={onHide} centered size="lg" className="epm-modal">
			<Modal.Header closeButton className="epm-modal-header">
				<div className="epm-progress-steps">
					{steps.map((s, i) => (
						<div
							key={s.key}
							className={`epm-progress-step ${i === currentIdx ? 'active' : ''} ${i < currentIdx ? 'done' : ''}`}
						>
							<div className="epm-step-dot">
								{i < currentIdx
									? <i className="fe fe-check"></i>
									: <i className={`fe ${s.icon}`}></i>}
							</div>
							<span className="epm-step-dot-label">{s.label}</span>
						</div>
					))}
				</div>
			</Modal.Header>

			<Modal.Body className="epm-modal-body">
				{step === 'auth' && <AuthStep onAuthenticated={handleAuthenticated} />}
				{step === 'mode' && (
					<ModeStep
						virtualPrice={virtualPrice}
						physicalPrice={physicalPrice}
						onSelect={handleModeSelect}
					/>
				)}
				{step === 'payment' && user && (
					<PaymentStep
						event={event}
						selectedMode={selectedMode}
						selectedPrice={selectedPrice}
						user={user}
						onSuccess={handlePaymentSuccess}
						onBack={() => setStep('mode')}
					/>
				)}
			</Modal.Body>

			<style>{`
				.epm-modal .modal-content {
					border: none;
					border-radius: 24px;
					overflow: hidden;
					box-shadow: 0 25px 60px rgba(0,0,0,0.15);
				}
				.epm-modal-header {
					background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
					border-bottom: 2px solid #e2e8f0;
					padding: 1.25rem 1.5rem;
				}
				.epm-progress-steps {
					display: flex;
					align-items: center;
					justify-content: center;
					gap: 2rem;
					width: 100%;
				}
				.epm-progress-step {
					display: flex;
					align-items: center;
					gap: 0.5rem;
					color: #94a3b8;
				}
				.epm-progress-step.active { color: #F68521; }
				.epm-progress-step.done { color: #10b981; }
				.epm-step-dot {
					width: 36px; height: 36px;
					border-radius: 50%;
					background: #e2e8f0;
					display: flex; align-items: center; justify-content: center;
					font-size: 0.875rem;
					transition: all 0.3s;
				}
				.epm-progress-step.active .epm-step-dot {
					background: linear-gradient(135deg, #F68521, #ff9d4d);
					color: white;
					box-shadow: 0 4px 12px rgba(246,133,33,0.3);
				}
				.epm-progress-step.done .epm-step-dot {
					background: #10b981;
					color: white;
				}
				.epm-step-dot-label { font-size: 0.8125rem; font-weight: 600; }
				.epm-modal-body { padding: 2rem; }

				/* Step shared */
				.epm-step-header { text-align: center; margin-bottom: 1.75rem; }
				.epm-step-icon-wrap {
					width: 80px; height: 80px; border-radius: 50%;
					background: linear-gradient(135deg, #F68521, #ff9d4d);
					display: flex; align-items: center; justify-content: center;
					margin: 0 auto 1rem;
					box-shadow: 0 8px 20px rgba(246,133,33,0.25);
				}
				.epm-step-icon { font-size: 2rem; color: white; }
				.epm-step-title { font-size: 1.375rem; font-weight: 800; color: #1e293b; margin-bottom: 0.375rem; }
				.epm-step-sub { font-size: 0.9375rem; color: #64748b; margin: 0; }

				/* Auth inputs */
				.epm-label { font-size: 0.875rem; font-weight: 600; color: #374151; margin-bottom: 0.375rem; display: block; }
				.epm-input {
					border: 2px solid #e2e8f0; border-radius: 10px;
					padding: 0.625rem 1rem; font-size: 0.9375rem;
					transition: border-color 0.2s;
				}
				.epm-input:focus { border-color: #F68521; box-shadow: 0 0 0 3px rgba(246,133,33,0.1); }
				.epm-privacy-note {
					background: #f8fafc; border-radius: 8px;
					padding: 0.75rem 1rem; display: flex; align-items: flex-start;
				}
				.epm-primary-btn {
					background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%);
					border: none; color: white; font-weight: 600; font-size: 1rem;
					padding: 0.75rem; border-radius: 12px;
					display: flex; align-items: center; justify-content: center;
					transition: opacity 0.2s;
				}
				.epm-primary-btn:hover:not(:disabled) { opacity: 0.9; color: white; background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%); }
				.epm-primary-btn:disabled { opacity: 0.65; background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%); color: white; }

				/* Mode cards */
				.epm-mode-cards { display: flex; flex-direction: column; gap: 1rem; }
				.epm-mode-card {
					display: flex; align-items: center; gap: 1rem;
					padding: 1.25rem 1.5rem; border-radius: 14px;
					border: 2px solid #e2e8f0; background: white;
					text-align: left; cursor: pointer; width: 100%;
					transition: all 0.2s;
				}
				.epm-mode-card:hover {
					border-color: #F68521;
					box-shadow: 0 4px 16px rgba(246,133,33,0.1);
					transform: translateY(-1px);
				}
				.epm-mode-icon {
					width: 48px; height: 48px; border-radius: 12px;
					display: flex; align-items: center; justify-content: center;
					font-size: 1.25rem; flex-shrink: 0;
				}
				.epm-mode-virtual { background: rgba(16,185,129,0.1); color: #10b981; }
				.epm-mode-physical { background: rgba(246,133,33,0.1); color: #F68521; }
				.epm-mode-card > div { flex: 1; }
				.epm-mode-label { font-size: 0.9375rem; font-weight: 700; color: #1e293b; }
				.epm-mode-price { font-size: 1.125rem; font-weight: 800; color: #F68521; margin: 2px 0; }
				.epm-mode-desc { font-size: 0.8125rem; color: #94a3b8; }
				.epm-mode-arrow { color: #cbd5e1; font-size: 1rem; flex-shrink: 0; }

				/* Order summary */
				.epm-order-summary {
					background: #f8fafc; border-radius: 14px;
					border: 1px solid #e2e8f0; padding: 1.25rem;
				}
				.epm-summary-row {
					display: flex; justify-content: space-between; align-items: flex-start;
					padding: 0.375rem 0;
					font-size: 0.9rem;
				}
				.epm-summary-label { color: #64748b; font-weight: 500; }
				.epm-summary-value { color: #1e293b; font-weight: 600; text-align: right; max-width: 60%; }
				.epm-summary-divider { border-top: 1.5px dashed #e2e8f0; margin: 0.75rem 0; }
				.epm-summary-total .epm-summary-label { font-weight: 700; color: #1e293b; }
				.epm-summary-total-amount { font-size: 1.25rem; font-weight: 800; color: #10b981; }

				/* Paystack button override */
				.epm-paystack-btn {
					background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
					border: none !important;
					color: white !important;
					font-weight: 700 !important;
					font-size: 1rem !important;
					padding: 0.8rem !important;
					border-radius: 12px !important;
					width: 100% !important;
					cursor: pointer !important;
					transition: opacity 0.2s !important;
				}
				.epm-paystack-btn:hover { opacity: 0.9 !important; }

				.epm-back-link {
					display: flex; align-items: center; justify-content: center;
					background: none; border: none; color: #94a3b8;
					font-size: 0.8125rem; font-weight: 600; cursor: pointer; width: 100%;
				}
				.epm-back-link:hover { color: #64748b; }
				.epm-secure-note {
					font-size: 0.8rem; color: #94a3b8;
					display: flex; align-items: center; justify-content: center;
				}
				.spinner-border { width: 1rem; height: 1rem; border-width: 2px; }
			`}</style>
		</Modal>
	);
};

export default EventPaymentModal;
